import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { useRefocus } from "@/lib/refocus";
import LoadingSkeleton from "@/components/app/LoadingSkeleton";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Handshake, Check, X, Eye, MapPin, Store, Building2, User, Phone, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const fmt = (iso) => { if(!iso) return "—"; try { const d=new Date(iso); return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); } catch { return "—"; } };

export default function AdminPartnersPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    setLoading(true);
    const url = tab === "pending" ? "/admin/partners/pending" : "/admin/partners";
    api.get(url)
      .then(r => { setItems(r.data); setLoaded(true); })
      .catch(e => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useRefocus(load);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/partners/${id}/approve`);
      toast.success(`Approved · ${data.partner_id}`);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Enter reason");
    try {
      await api.post(`/admin/partners/${rejecting.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Rejected");
      setRejecting(null); setRejectReason(""); load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const filtered = tab === "pending" ? items : items.filter(p => tab === "all" || p.verification_status === tab);

  return (
    <div className="space-y-6" data-testid="admin-partners-page">
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center"><Handshake className="w-5 h-5"/></div>
          <div>
            <div className="font-heading text-xl text-white">Partner Requests</div>
            <div className="text-sm text-slate-500">Verify office & approve DSA / Connector / Agent applications.</div>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {[["pending","Pending"],["verified","Verified"],["rejected","Rejected"],["all","All"]].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} data-testid={`partners-tab-${k}`}
              className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest ${tab===k?"bg-amber-500 text-slate-900":"bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="card-surface p-5">
        {loading && !loaded ? <LoadingSkeleton rows={4} label="Loading partner requests…" testid="partners-loading"/> :
         filtered.length === 0 ? <div className="text-slate-500 text-center py-10">No {tab} partner requests.</div> :
         (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/10">
                <tr>
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">District / Branch</th>
                  <th className="text-left py-2 px-2">Office</th>
                  <th className="text-left py-2 px-2">Photos</th>
                  <th className="text-left py-2 px-2">Applied</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`partner-request-row-${p.id}`}>
                    <td className="py-3 px-2">
                      <div className="text-white font-semibold">{p.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono-alt">{p.phone} · {p.experience_type}</div>
                      {p.partner_id && <div className="text-[10px] text-amber-400 font-mono-alt mt-0.5">{p.partner_id}</div>}
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-slate-200">{p.district}</div>
                      <div className="text-[11px] text-slate-500">{p.branch_name} · <span className="font-mono-alt">{p.branch_code}</span></div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-slate-200 flex items-center gap-1"><Store className="w-3 h-3 text-slate-500"/>{p.office_name}</div>
                      <div className="text-[11px] text-slate-500">{p.office_type}</div>
                      <div className="text-[11px] text-slate-500 max-w-xs truncate" title={p.office_address}>{p.office_address}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex gap-1">
                        <button onClick={()=>setViewer({title:"Office Photo (Shop Front)", b64: p.office_photo_b64})} className="text-cyan-400 text-xs hover:underline" data-testid={`view-photo-${p.id}`}>Photo</button>
                        <span className="text-slate-600">·</span>
                        <button onClick={()=>setViewer({title:"Office Proof", b64: p.office_proof_b64})} className="text-cyan-400 text-xs hover:underline" data-testid={`view-proof-${p.id}`}>Proof</button>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-[11px] text-slate-400">{fmt(p.created_at)}</td>
                    <td className="py-3 px-2 text-right">
                      {p.verification_status === "verified" && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 text-[10px] px-2 py-1 font-bold uppercase tracking-widest"><Check className="w-3 h-3"/> Verified</span>}
                      {p.verification_status === "rejected" && <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 text-red-300 border border-red-500/50 text-[10px] px-2 py-1 font-bold uppercase tracking-widest"><X className="w-3 h-3"/> Rejected</span>}
                      {p.verification_status === "pending" && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" data-testid={`approve-${p.id}`} onClick={()=>approve(p.id)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-xs h-8 rounded-full font-bold"><Check className="w-3 h-3 mr-1"/>Approve</Button>
                          <Button size="sm" data-testid={`reject-${p.id}`} onClick={()=>setRejecting(p)} variant="ghost" className="bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs h-8 rounded-full font-bold"><X className="w-3 h-3 mr-1"/>Reject</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </div>

      {/* Image viewer */}
      <Dialog open={!!viewer} onOpenChange={(o)=>!o && setViewer(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader><DialogTitle className="text-amber-400">{viewer?.title}</DialogTitle></DialogHeader>
          {viewer?.b64 ? <img src={viewer.b64} alt="preview" className="max-h-[70vh] w-full object-contain rounded-lg"/> : <div className="text-slate-500 text-center py-10">No image</div>}
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <Dialog open={!!rejecting} onOpenChange={(o)=>{if(!o){setRejecting(null); setRejectReason("");}}}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-red-400">Reject Partner Application</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-300">Applicant: <b>{rejecting?.name}</b> · {rejecting?.phone}</div>
            <Textarea data-testid="reject-reason" value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="Reason (will be shown to applicant)" className="bg-slate-950 border-slate-700"/>
            <Button data-testid="confirm-reject" onClick={reject} className="w-full h-11 bg-red-500 hover:bg-red-400 text-white font-bold rounded-full">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
